import { getOrderItemStatsQuery } from '@/api/stats/statsQueries';
import { DateRangePickerButton } from '@/components/common/FilterButtons';
import {
  SlimTable,
  SlimTableBody,
  SlimTableCell,
  SlimTableHead,
  SlimTableHeader,
  SlimTableRow,
} from '@/components/common/SlimTable';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { DashboardTableAccordion } from '../DashboardTableAccordion';

interface SupplementFilters {
  dateFrom: string;
  dateTo: string;
}

const INITIAL_FILTERS: SupplementFilters = {
  dateFrom: '',
  dateTo: '',
};

export const StatisticsSupplements = ({
  accordionOpenValue,
}: {
  accordionOpenValue?: string;
}) => {
  const [filters, setFilters] = useState<SupplementFilters>(INITIAL_FILTERS);
  const [dateFrom, setDateFrom] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState('');
  const [isDateActive, setIsDateActive] = useState(false);

  const handleAccordionOpen = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setDateFrom(format(new Date(), 'yyyy-MM-dd'));
    setDateTo('');
    setIsDateActive(false);
  }, []);

  const apiFilters = {
    date_from: filters.dateFrom || undefined,
    date_to: filters.dateTo || undefined,
  };

  const { data: itemStats, isLoading } = useQuery(
    getOrderItemStatsQuery(apiFilters),
  );

  const sortedItems = useMemo(() => {
    const items = itemStats?.data;
    if (!items) return [];
    return [...items]
      .map((item, index) => ({
        ...item,
        id: index,
        quantity: item.total_quantity,
      }))
      .sort((a, b) => b.quantity - a.quantity);
  }, [itemStats]);

  const handleDateRangeChange = (from: string, to: string) => {
    setDateFrom(from);
    setDateTo(to);
    setIsDateActive(true);
    setFilters({ dateFrom: from, dateTo: to });
  };

  return (
    <DashboardTableAccordion
      value="statistics-supplements"
      title="Statistics supplements"
      accordionOpenValue={accordionOpenValue}
      onOpen={handleAccordionOpen}
      data={sortedItems}
      isLoading={isLoading}
      emptyMessage="No supplement statistics found."
      loadingMessage="Loading statistics..."
      filtersComponent={
        <div className="flex flex-wrap items-end gap-3">
          <DateRangePickerButton
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateRangeChange={handleDateRangeChange}
            isActive={isDateActive}
          />
        </div>
      }
      customTable={
        <SlimTable>
          <SlimTableHeader>
            <SlimTableRow>
              <SlimTableHead>Supplement</SlimTableHead>
              <SlimTableHead>Dose</SlimTableHead>
              <SlimTableHead className="text-right">Quantity</SlimTableHead>
            </SlimTableRow>
          </SlimTableHeader>
          <SlimTableBody>
            {sortedItems.map((item) => (
              <SlimTableRow key={item.id}>
                <SlimTableCell>{item.name}</SlimTableCell>
                <SlimTableCell>{item.strength}</SlimTableCell>
                <SlimTableCell className="text-right">
                  {item.quantity}
                </SlimTableCell>
              </SlimTableRow>
            ))}
          </SlimTableBody>
        </SlimTable>
      }
    />
  );
};
